/**
 * Subscription domain models — the strongly-typed vocabulary of the S1–S6 +
 * renew conversion flow (see docs/SUBSCRIPTION-STATE-MACHINE-v3.38.md). Pure
 * data — no Angular, no v1. Latin numerals, Arabic-first copy lives in i18n.
 *
 * Tier machine: free → trial (7d, auto-expires) → premium (monthly) ⇄ cancel.
 * A trial grants full Premium access (`isPremium` true for trial OR premium).
 */

import { Language } from '@core/i18n/domain/language';

export type SubscriptionTier = 'free' | 'trial' | 'premium';

/**
 * Canonical screens. S1 is conditional on tier (free pitch / trial / premium).
 *   S2 · plan picker (doc §8.4)   S3 · trial confirm   S4 · cancel flow
 *   S5 · end-of-trial             S6 · memory ribbon   S7 · checkout summary
 *   S8 · manage & billing (auto-renew · method · invoices)
 */
export type SubscriptionScreen = 'S1' | 'S2' | 'S3' | 'S4' | 'S5' | 'S6' | 'S7' | 'S8' | 'renew';

/** Celebratory transition flavours. */
export type TransitionKind = 'upgrade' | 'renew' | 'trial' | 'cancel';

/**
 * The server's full 6-state machine (SUBSCRIPTION-API doc §2). The v2 UI mostly
 * works off the coarser {@link SubscriptionTier}, but the raw state is carried in
 * the snapshot so the cancelled-grace (still-premium) state can be distinguished
 * from active-premium and drive the reactivate flow. `null` ⇒ unknown (offline /
 * local mock — no server state).
 */
export type SubscriptionState =
  | 'free_new'
  | 'free_normal'
  | 'trial_active'
  | 'premium_active'
  | 'cancelled_grace'
  | 'post_cancellation';

/** Billing cadence (doc §3). */
export type SubscriptionCycle = 'monthly' | 'annual';

/**
 * How the subscription is charged. `wallet` settles from the Talbinah balance
 * (no gateway hop); everything else hands off to the hosted checkout.
 * Server-driven — unknown values are carried through as `card` (the default).
 */
export type PaymentMethodKind = 'card' | 'wallet' | 'apple_pay' | 'mada' | 'stc_pay';

/** Plan limits / feature flags (doc §3.1). `-1` = unlimited. camelCase domain mirror of the DTO. */
export interface SubscriptionPolicy {
  messageLimit: number;
  voiceLimitWeeklyMin: number;
  memoryEnabled: boolean;
  proactiveCapDaily: number;
  /** Days Khoyek remembers, `-1` = permanent; null/absent until the API sends it (GAP 01). */
  memoryDays?: number | null;
  /** SAR per group support session, `0` = free; null/absent until the API sends it (GAP 01). */
  groupSessionPrice?: number | null;
  /** Replies jump the queue; null/absent until the API sends it (GAP 01). */
  priorityReply?: boolean | null;
}

/**
 * The Free-vs-Premium values used for any policy field the API leaves out or
 * sends as null (GAP 01) — the handoff 07 table, except voice minutes, which
 * follow the server (product decision 2026-10-14: free 5 · Premium 60 per week).
 * A server value always wins, field by field ({@link withPolicyFallback}).
 */
export const FREE_POLICY_FALLBACK: SubscriptionPolicy = {
  messageLimit: 20,
  voiceLimitWeeklyMin: 5,
  memoryEnabled: false,
  proactiveCapDaily: 0,
  memoryDays: 7,
  groupSessionPrice: 25,
  priorityReply: false,
};

export const PREMIUM_POLICY_FALLBACK: SubscriptionPolicy = {
  messageLimit: -1,
  voiceLimitWeeklyMin: 60,
  memoryEnabled: true,
  proactiveCapDaily: 0,
  memoryDays: 30,
  groupSessionPrice: 0,
  priorityReply: true,
};

/** `policy` with every missing / null field taken from `fallback`. */
export function withPolicyFallback(
  policy: SubscriptionPolicy | null | undefined,
  fallback: SubscriptionPolicy,
): SubscriptionPolicy {
  return {
    messageLimit: policy?.messageLimit ?? fallback.messageLimit,
    voiceLimitWeeklyMin: policy?.voiceLimitWeeklyMin ?? fallback.voiceLimitWeeklyMin,
    memoryEnabled: policy?.memoryEnabled ?? fallback.memoryEnabled,
    proactiveCapDaily: policy?.proactiveCapDaily ?? fallback.proactiveCapDaily,
    memoryDays: policy?.memoryDays ?? fallback.memoryDays,
    groupSessionPrice: policy?.groupSessionPrice ?? fallback.groupSessionPrice,
    priorityReply: policy?.priorityReply ?? fallback.priorityReply,
  };
}

/** Today's metered usage (doc §3.1/§6) — resets midnight UTC. */
export interface SubscriptionUsage {
  messagesSent: number;
  voiceSeconds: number;
}

/**
 * The plan terms locked at subscribe time (doc §3.1/§3.2).
 *
 * `name` is the SERVER-LOCALIZED plan title: the live `/plans` endpoint returns a
 * single `name` switched by `Accept-Language` (verified against
 * `api.dev.talbinah.net`, 2026-08-04) — the doc's `name_ar`/`name_en` pair is
 * historical. The mapper accepts either shape and always lands here.
 */
export interface PlanRef {
  id: number;
  name: string;
  price: number;
  cycle: SubscriptionCycle;
}

/** A subscribable plan from the public catalog (`GET /plans`), with its limits. */
export interface SubscriptionPlan extends PlanRef {
  policy: SubscriptionPolicy | null;
}

/** `GET /plans` read: the plans, plus the FREE tier's policy once the API sends it (GAP 01). */
export interface SubscriptionCatalog {
  plans: SubscriptionPlan[];
  freePolicy: SubscriptionPolicy | null;
}

/** Outcome of `POST /quote` — the pre-checkout money breakdown (wallet + promo). */
export interface SubscriptionQuote {
  planId: number;
  cycle: SubscriptionCycle;
  /** ISO currency code as the server names it (`SAR` by default). */
  currency: string;
  /** Plan price before any deduction. */
  subtotal: number;
  /** Promo/coupon discount applied (>= 0). */
  discount: number;
  /** Amount settled from the Talbinah wallet balance (>= 0). */
  walletApplied: number;
  /** What the user actually pays at the gateway (>= 0). */
  total: number;
  /** The promo code the quote was computed with, when one was sent. */
  promoCode: string | null;
  /** Server's verdict on that code — null when no code was sent. */
  promoValid: boolean | null;
  /** Server's localized note about the code / breakdown; display-ready. */
  promoMessage: string | null;
  /** The wallet balance the server used, when it reports one. */
  walletBalance: number | null;
}

/** Settlement state of a billing document (`GET /invoices`). */
export type InvoiceStatus = 'paid' | 'pending' | 'failed' | 'refunded' | 'unknown';

/** One row of billing history (`GET /invoices`). */
export interface SubscriptionInvoice {
  id: string;
  /** Human invoice number when the server issues one. */
  number: string | null;
  /** epoch ms — issue/charge date (0 when the server omits it). */
  issuedAt: number;
  amount: number;
  currency: string;
  status: InvoiceStatus;
  /** Hosted receipt/PDF link, when available. */
  url: string | null;
  /** Localized plan title at charge time. */
  planName: string | null;
  cycle: SubscriptionCycle | null;
  /**
   * A renewal the server charged on its own from the saved card (`initiator: mit`,
   * SUBSCRIPTION-RECURRING R4) — not a payment the user made. Declined ones are
   * listed too, so the row says which kind of charge failed.
   */
  automatic: boolean;
}

/** A saved payment card (`GET /cards`). */
export interface SubscriptionCard {
  id: string;
  /** `visa` / `mada` / … — display-ready, lower-cased by the mapper. */
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  isDefault: boolean;
}

/**
 * What `POST /subscribe` | `/reactivate` asked us to do next.
 *   · `redirect`  — hand the tab to the hosted checkout (`url`)
 *   · `activated` — nothing to pay (wallet covered it) and the server already
 *                   flipped the state; the caller re-reads the snapshot
 *   · `pending`   — accepted, but activation lands via the gateway webhook and
 *                   there is no URL to send the user to
 */
export type CheckoutOutcome =
  | { readonly kind: 'redirect'; readonly url: string }
  | { readonly kind: 'activated' }
  | { readonly kind: 'pending' };

/**
 * What `POST /payment-method` handed back for «تغيير البطاقة» (SUBSCRIPTION-RECURRING
 * R3): a hosted, CAPTURED verification charge that is refunded in full. The new
 * card is stored only once that charge captures — this is never "card changed".
 */
export interface CardUpdateCheckout {
  /** Hosted gateway page; null when the server gave none (nothing to open). */
  readonly url: string | null;
  /** Server-localized notice stating the charge + refund — shown BEFORE the page opens. */
  readonly message: string | null;
  /** The amount actually charged for the check, when stated. */
  readonly amountDue: number | null;
  readonly currency: string;
  /** Whether `amountDue` is refunded in full after capture. */
  readonly willRefund: boolean;
}

/** `POST /payments/tap/verify` — the fast-path check of a charge the gateway returned from. */
export interface ChargeVerification {
  /** Whether the charge left a full renewal mandate; null when the flow doesn't say. */
  readonly mandateCaptured: boolean | null;
  /** Why no mandate was stored (`mandate_not_issued`, …), when the server says. */
  readonly reason: string | null;
}

/** Why the tab was handed to the gateway — decides what "confirmed" means on return. */
export type CheckoutPurpose = 'subscribe' | 'card_update';

/** A checkout handed off to the gateway, awaiting the user's return. */
export interface PendingCheckout {
  readonly planId: number;
  readonly cycle: SubscriptionCycle;
  /** epoch ms when the hand-off happened — drives the staleness TTL. */
  readonly startedAt: number;
  /**
   * `card_update` is confirmed by a stored mandate, not by premium (the user is
   * already premium). Absent on markers written before it existed ⇒ `subscribe`.
   */
  readonly purpose?: CheckoutPurpose;
}

/** The card on file as the subscription reports it (`payment_method: { brand, last4 }`). */
export interface PaymentCardOnFile {
  /** As the server names it (`VISA`); {@link cardLabel} formats it for display. */
  brand: string | null;
  last4: string | null;
}

/** Persisted snapshot (mirrors localStorage tier + sibling timestamps). */
export interface SubscriptionSnapshot {
  tier: SubscriptionTier;
  /** epoch ms — trial end (0 if none). */
  trialExpiresAt: number;
  /** Trial length the server states (`trial_days`); null/absent → {@link SUBSCRIPTION_TRIAL_DAYS} (GAP 02). */
  trialDays?: number | null;
  /** epoch ms — paid renewal date (0 if none). */
  nextBillAt: number;
  // ── real-API enrichment (doc §3.1). null/0/undefined from the local mock. ──
  /** Raw server state; null when offline / mock. */
  state?: SubscriptionState | null;
  /** epoch ms — grace end; `>0` ONLY while `cancelled_grace` (keeps premium 30d). */
  graceEndsAt?: number;
  /** Whether a trial was already consumed — gates the one-time trial CTA (§8.3). */
  hadTrial?: boolean;
  /** The plan locked on the subscription (real price/cycle/name); null when none. */
  plan?: PlanRef | null;
  /** Daily/weekly caps + feature flags; null when unknown. */
  policy?: SubscriptionPolicy | null;
  /** Today's metered usage; null when unknown. */
  usageToday?: SubscriptionUsage | null;
  /** Billing cadence of the running subscription (mirrors `plan.cycle`). */
  cycle?: SubscriptionCycle | null;
  /**
   * Auto-renew switch (`POST /auto-renew`); null when the server doesn't say.
   * The server reports `false` whenever no mandate is stored, so it is honest.
   */
  autoRenew?: boolean | null;
  /**
   * A full renewal mandate (customer + card + agreement) is stored. `false` ⇒
   * renewal cannot run until the card is updated; null when the server doesn't say.
   */
  mandateOnFile?: boolean | null;
  /** Settlement method on file (`POST /payment-method`); null when unset. */
  paymentMethod?: PaymentMethodKind | null;
  /** The card on file (brand + last4) when the server reports one — may exist without a mandate. */
  paymentCard?: PaymentCardOnFile | null;
  /** Id of the saved card bound to the subscription, when one is. */
  cardId?: string | null;
  /**
   * epoch ms — when the CURRENT premium access ends, as the server chose it
   * (trial / period / grace end by state). 0 when not premium or not reported.
   */
  premiumEndsAt?: number;
  /**
   * The server's calendar-day countdown to {@link premiumEndsAt}: `0` = ends
   * today, `null` = not premium (hide the badge). `undefined` = the backend did
   * not send the key at all (predates it) — the only case the client derives it.
   * @see premiumDaysLeft
   */
  daysLeft?: number | null;
  /**
   * The ORGANIZATION grant covering this member (B2B §9.5), or null for everyone
   * else. Read unconditionally — membership is structural, not a client flag.
   */
  orgGrant?: OrgGrant | null;
}

/**
 * An organization is covering this member's use of the product (B2B §9.5).
 *
 * {@link hidePurchase} is the ONE flag that switches every commerce surface off.
 * Prefer it over inferring from a zero price: a zero cannot say who paid, and a
 * covered member who sees an upgrade CTA is being asked to buy what they already
 * have.
 */
export interface OrgGrant {
  active: boolean;
  /** Ready to render («مُهدى من جامعة القصيم»). Never re-translated (§1.5). */
  label: string | null;
  /** The organization's own name, for copy that names it apart from the badge. */
  organization: string | null;
  /** ISO 8601, or null — and NULL MEANS OPEN-ENDED, not missing. */
  endsAt: string | null;
  daysLeft: number | null;
  /** True → hide every purchase / upgrade CTA in the app. */
  hidePurchase: boolean;
  /** Ready-to-render explanation of what the grant covers. */
  note: string | null;
}

export const INITIAL_SUBSCRIPTION: SubscriptionSnapshot = {
  tier: 'free',
  trialExpiresAt: 0,
  nextBillAt: 0,
  state: null,
  graceEndsAt: 0,
  hadTrial: false,
  plan: null,
  policy: null,
  usageToday: null,
  cycle: null,
  autoRenew: null,
  mandateOnFile: null,
  paymentMethod: null,
  paymentCard: null,
  cardId: null,
  premiumEndsAt: 0,
};

/**
 * Pricing + trial constants (§15.7/§15.8) — price is 9 SAR/month everywhere.
 * `SUBSCRIPTION_TRIAL_DAYS` stands in until `GET /subscription` sends `trial_days` (GAP 02).
 */
export const SUBSCRIPTION_PRICE_SAR = 9;
export const SUBSCRIPTION_TRIAL_DAYS = 7;
export const DAY_MS = 864e5;

/** A Premium benefit row (S1 free pitch). */
export interface BenefitItem {
  readonly icon: string;
  readonly titleKey: string;
  readonly subKey: string;
  /**
   * The row quotes a server limit (`voice`): its copy is resolved from the
   * plan / user policy by the facade instead of the static keys above.
   */
  readonly limit?: PolicyLimitKind;
}

/**
 * One cell of the Free-vs-Premium table.
 *  · `spec`   — a product-spec value (handoff 07 table) no API reports yet.
 *  · `policy` — a message / voice limit read from the tier policy.
 *  · `perk`   — memory days · group session price · reply priority from the tier policy.
 *  · `tools`  — «الكل ({n})» with the `GET /tools` count, «الكل» while unknown.
 */
export type CompareCell =
  | { readonly kind: 'spec'; readonly key: string }
  | { readonly kind: 'policy'; readonly limit: PolicyLimitKind; readonly fallbackKey?: string }
  | { readonly kind: 'perk'; readonly perk: PolicyPerkKind }
  | { readonly kind: 'tools' };

/** A Free-vs-Premium row read off the policy's GAP 01 fields. */
export type PolicyPerkKind = 'memory' | 'groups' | 'priority';

/** A free-vs-premium comparison row (feature label + both cells). */
export interface CompareItem {
  readonly featureKey: string;
  readonly free: CompareCell;
  readonly premium: CompareCell;
}

/** A compare row resolved for display (labels already translated). */
export interface CompareRowView {
  readonly featureKey: string;
  readonly feature: string;
  readonly free: string;
  readonly premium: string;
}

/** A benefit row resolved for display; `sub` null ⇒ no sub-line. */
export interface BenefitView {
  readonly icon: string;
  readonly title: string;
  readonly sub: string | null;
}

/** A copy line (unlocked list · cancel changes); `limit` ⇒ its copy quotes the policy limit. */
export interface LimitCopyItem {
  readonly key: string;
  readonly limit?: PolicyLimitKind;
}

/** Gregorian month names per language (never Hijri ar-SA) — for the bill / end date labels. */
const MONTHS: Readonly<Record<Language, readonly string[]>> = {
  [Language.Ar]: [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
  ],
  [Language.En]: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ],
};

/**
 * Format an epoch-ms date as `"<d> <month> <yyyy>"` with Latin numerals, in the
 * given language (`Language.Ar` default). Pass the live language so the label
 * switches with the toggle (the facade reads `currentLanguage()` inside a computed).
 */
export function formatBillDate(ms: number, lang: Language = Language.Ar): string {
  if (!ms) return '';
  const d = new Date(ms);
  const months = lang === Language.En ? MONTHS[Language.En] : MONTHS[Language.Ar];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

// ─── premium countdown (KHOYEK-SUBSCRIPTION-DAYS-LEFT) ────────────────────

/**
 * Which sentence the countdown badge speaks. `renews` is a RENEWAL date, not an
 * end date — a `premium_active` user with auto-renew on must never read "ends".
 */
export type PremiumCountdownKind = 'trial' | 'renews' | 'ends' | 'grace';

/**
 * Calendar days from `now` to `endMs`, counted on the device's calendar: `0` when
 * it ends later TODAY. `null` when either instant is unknown or the end already
 * passed. Only the fallback for a backend that predates `days_left` — the server's
 * own count always wins (see {@link premiumDaysLeft}).
 */
export function calendarDaysUntil(endMs: number, now: number): number | null {
  if (!endMs || !now || !Number.isFinite(endMs) || !Number.isFinite(now)) return null;
  if (endMs <= now) return null;
  const end = new Date(endMs);
  const today = new Date(now);
  // Midnight-to-midnight on the local calendar, compared as UTC day numbers so a
  // DST shift inside the window can never round a whole day away.
  const endDay = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  const nowDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((endDay - nowDay) / DAY_MS);
}

/** The end the countdown runs to, picked by state the way the server picks `premium_ends_at`. */
function legacyPremiumEnd(snapshot: SubscriptionSnapshot): number {
  switch (snapshot.state ?? null) {
    case 'trial_active':
      return snapshot.trialExpiresAt;
    case 'premium_active':
      return snapshot.nextBillAt;
    case 'cancelled_grace':
      return snapshot.graceEndsAt || snapshot.nextBillAt;
    case null:
      // Cache-only (cold start / offline) — no server state yet, only the tier.
      if (snapshot.tier === 'trial') return snapshot.trialExpiresAt;
      if (snapshot.tier === 'premium') return snapshot.nextBillAt;
      return 0;
    default:
      return 0;
  }
}

/**
 * Days left on the user's OWN premium access, or `null` ⇒ hide the badge.
 *
 * The server's `days_left` is authoritative whenever it was sent — `null`
 * included, which means "not premium" and is never second-guessed from `state`
 * or `is_premium`. Only when the key is ABSENT (a backend that predates it) is
 * the count derived locally, from the same end the server would pick.
 */
export function premiumDaysLeft(snapshot: SubscriptionSnapshot, now: number): number | null {
  if (snapshot.daysLeft !== undefined) {
    return snapshot.daysLeft == null ? null : Math.max(0, snapshot.daysLeft);
  }
  return calendarDaysUntil(legacyPremiumEnd(snapshot), now);
}

/** The countdown copy for the current state, or `null` when there is no premium access. */
export function premiumCountdownKind(snapshot: SubscriptionSnapshot): PremiumCountdownKind | null {
  const state = snapshot.state ?? null;
  const tier = snapshot.tier;
  if (state === 'trial_active' || (state === null && tier === 'trial')) return 'trial';
  if (state === 'cancelled_grace') return 'grace';
  if (state === 'premium_active' || (state === null && tier === 'premium')) {
    return snapshot.autoRenew === true ? 'renews' : 'ends';
  }
  return null;
}

/** Trial considered active (Premium access) iff tier is trial OR premium. */
export function isPremiumTier(tier: SubscriptionTier): boolean {
  return tier === 'trial' || tier === 'premium';
}

// ─── policy / usage arithmetic (doc §6) ───────────────────────────────────

/** The sentinel the API uses for "no cap" on every policy limit (doc §3.1). */
export const UNLIMITED = -1;

/** A limit is uncapped when it is the `-1` sentinel (or nonsensical/negative). */
export function isUnlimited(limit: number | null | undefined): boolean {
  return limit == null || limit < 0;
}

/** Which policy limit a Free-vs-Premium copy line reads. */
export type PolicyLimitKind = 'messages' | 'voice';

/**
 * A policy limit ready for display: `unlimited` (the `-1` sentinel), a capped
 * count, or `null` — unknown (loading / not reported), which renders NO number
 * rather than a guessed one.
 */
export type LimitValue = { readonly kind: 'unlimited' } | { readonly kind: 'count'; readonly n: number };

/** Read one limit off a policy; `null` when there is no policy to read. */
export function policyLimit(policy: SubscriptionPolicy | null | undefined, kind: PolicyLimitKind): LimitValue | null {
  if (!policy) return null;
  const raw = kind === 'messages' ? policy.messageLimit : policy.voiceLimitWeeklyMin;
  if (raw == null || !Number.isFinite(raw)) return null;
  return raw < 0 ? { kind: 'unlimited' } : { kind: 'count', n: raw };
}

/**
 * The FREE tier's limits: `free_policy` from `GET /plans` when sent, else the
 * user's own policy while the server says they are on free (`state` present ⇒
 * a real read, never the cold cache). Any field still missing comes from
 * {@link FREE_POLICY_FALLBACK}, so a trial / Premium user sees the free column too.
 */
export function freeTierPolicy(
  snapshot: SubscriptionSnapshot,
  catalogFree: SubscriptionPolicy | null = null,
): SubscriptionPolicy {
  const own = snapshot.state != null && snapshot.tier === 'free' ? (snapshot.policy ?? null) : null;
  return withPolicyFallback(catalogFree ?? own, FREE_POLICY_FALLBACK);
}

/**
 * The PREMIUM limits: the user's own running policy when the server says they
 * are on Premium, else the catalog plan's (`GET /subscription/plans`) for the
 * chosen cycle, else any plan that carries one. Missing fields come from
 * {@link PREMIUM_POLICY_FALLBACK}.
 */
export function premiumTierPolicy(
  snapshot: SubscriptionSnapshot,
  plans: readonly SubscriptionPlan[],
  cycle: SubscriptionCycle,
): SubscriptionPolicy {
  const own = snapshot.state != null && isPremiumTier(snapshot.tier) ? (snapshot.policy ?? null) : null;
  const plan = planForCycle(plans, cycle)?.policy ?? plans.find((p) => p.policy)?.policy ?? null;
  return withPolicyFallback(own ?? plan, PREMIUM_POLICY_FALLBACK);
}

/** Trial length: the server's `trial_days` when it is a positive number, else {@link SUBSCRIPTION_TRIAL_DAYS}. */
export function trialLengthDays(snapshot: SubscriptionSnapshot): number {
  const days = snapshot.trialDays;
  return days != null && days > 0 ? days : SUBSCRIPTION_TRIAL_DAYS;
}

/**
 * Messages the user may still send today, or `null` when uncapped/unknown.
 * Mirrors the doc's `canSendMessage` guard — the server enforces the same cap
 * and answers `429` past it (crisis messages are never throttled).
 */
export function messagesRemaining(
  policy: SubscriptionPolicy | null | undefined,
  usage: SubscriptionUsage | null | undefined,
): number | null {
  if (!policy || isUnlimited(policy.messageLimit)) return null;
  return Math.max(0, policy.messageLimit - (usage?.messagesSent ?? 0));
}

/** Fraction (0..100) of today's message cap consumed; `null` when uncapped/unknown. */
export function usagePercent(
  policy: SubscriptionPolicy | null | undefined,
  usage: SubscriptionUsage | null | undefined,
): number | null {
  if (!policy || isUnlimited(policy.messageLimit) || policy.messageLimit === 0) return null;
  const used = Math.max(0, usage?.messagesSent ?? 0);
  return Math.min(100, Math.round((used / policy.messageLimit) * 100));
}

/**
 * Whole-percent saving of the annual plan against 12× the monthly one; `0` when
 * either price is missing or annual isn't actually cheaper (never invent a
 * discount badge the pricing doesn't support).
 */
export function annualSavingsPct(monthlyPrice: number, annualPrice: number): number {
  if (!(monthlyPrice > 0) || !(annualPrice > 0)) return 0;
  const yearlyAtMonthly = monthlyPrice * 12;
  if (annualPrice >= yearlyAtMonthly) return 0;
  return Math.round(((yearlyAtMonthly - annualPrice) / yearlyAtMonthly) * 100);
}

/**
 * The one-time free trial is offered only from a free state AND only when no
 * trial was ever consumed (doc §8.3 — the server rejects a second one with 422).
 */
export function canStartTrial(snapshot: SubscriptionSnapshot): boolean {
  if (snapshot.hadTrial) return false;
  const state = snapshot.state;
  // No server state (cold cache / offline) ⇒ fall back to the coarse tier.
  if (state == null) return snapshot.tier === 'free';
  return state === 'free_new' || state === 'free_normal';
}

/** Pick the plan matching `cycle`, else the first one; `null` for an empty catalog. */
export function planForCycle(
  plans: readonly SubscriptionPlan[],
  cycle: SubscriptionCycle,
): SubscriptionPlan | null {
  if (!plans.length) return null;
  return plans.find((p) => p.cycle === cycle) ?? plans[0]!;
}

/** A checkout hand-off older than this is treated as abandoned, not "verifying". */
export const CHECKOUT_PENDING_TTL_MS = 30 * 60_000;

/** Is a stored hand-off still worth polling the server about? */
export function isCheckoutFresh(pending: PendingCheckout | null, now: number): boolean {
  if (!pending?.startedAt) return false;
  const age = now - pending.startedAt;
  return age >= 0 && age < CHECKOUT_PENDING_TTL_MS;
}

/** Money for display — trims a trailing `.00` so `9` reads as `9`, `86.5` intact. */
export function formatAmount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '';
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
}

/** `**** 4242` style card label; falls back to the brand alone, then an em dash. */
export function cardLabel(card: Pick<SubscriptionCard, 'brand' | 'last4'> | null | undefined): string {
  if (!card) return '';
  const brand = card.brand ? card.brand.toUpperCase() : '';
  if (card.last4) return brand ? `${brand} •••• ${card.last4}` : `•••• ${card.last4}`;
  return brand;
}
